import { motion } from 'framer-motion';
import { ShieldCheck, ServerCog, BrainCircuit, Landmark, Code2, Lock, Headphones } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const items = [
  { id: 'data', icon: ServerCog },
  { id: 'noTraining', icon: BrainCircuit },
  { id: 'gdpr', icon: Landmark },
  { id: 'openSource', icon: Code2 },
  { id: 'encryption', icon: Lock },
  { id: 'support', icon: Headphones },
] as const;

const TrustSection = () => {
  const { t } = useTranslation('editor');

  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-40 mask-fade-b" />
      <div className="pointer-events-none absolute -top-32 right-0 w-[600px] h-[400px] rounded-full bg-emerald-500/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 border border-emerald-400/25 bg-emerald-400/[0.06] text-emerald-200">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="text-sm font-semibold tracking-wide">{t('trust.badge')}</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">{t('trust.title')}</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">{t('trust.subtitle')}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(({ id, icon: Icon }, i) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08 }}
              className="group glass-card rounded-3xl p-7 hover:border-emerald-400/30 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 border border-emerald-300/20 flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110">
                <Icon className="h-5 w-5 text-emerald-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t(`trust.items.${id}.title`)}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{t(`trust.items.${id}.description`)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
